'use client'

import type React from 'react'
import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { styled, type Theme, type SxProps } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import CircularProgress from '@mui/material/CircularProgress'
import SendIcon from '@mui/icons-material/Send'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import CancelIcon from '@mui/icons-material/Cancel'
import Tooltip from '@mui/material/Tooltip'
import SwitchCameraIcon from '@mui/icons-material/SwitchCamera'
// Import the centralized type
import type { UploadedFileAttachment } from '@/core/models/medconnect-agent.model'

// Styled component for file upload input (from ChatInterface.tsx)
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

// Modal style for camera (from ChatInterface.tsx)
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 400 },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
}



interface AgentChatInputAreaProps {
  onSendMessage: (messageContent: string, files: File[]) => Promise<void>
  isLoading: boolean
  placeholder?: string
  sx?: SxProps<Theme>
  /** Hide the "Press Enter to send..." instruction text */
  hideInstructions?: boolean
  /** Compact mode — smaller controls. The camera button still shows whenever a webcam (getUserMedia) is available. */
  compact?: boolean
}

export function AgentChatInputArea({ onSendMessage, isLoading, placeholder, sx, hideInstructions = false, compact = false }: AgentChatInputAreaProps) {
  const { t } = useTranslation()
  const [messageInput, setMessageInput] = useState<string>('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([])
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chatInputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (chatInputRef.current) {
      // We need to reset the height to 'auto' before setting the scrollHeight.
      // This allows the textarea to shrink if text is deleted.
      chatInputRef.current.style.height = 'auto'
      chatInputRef.current.style.height = `${chatInputRef.current.scrollHeight}px`
    }
  }, [messageInput])

  // Cleanup effect to stop camera stream on unmount
  useEffect(() => {
    return () => {
      // Stop any active MediaStream when component unmounts
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop())
        setCameraStream(null)
      }
      // Also check videoRef for any remaining stream
      if (videoRef.current?.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream
        stream.getTracks().forEach((track) => track.stop())
        videoRef.current.srcObject = null
      }
      // Reset camera state
      setCameraOpen(false)
    }
  }, [cameraStream])

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files)
      setSelectedFiles((prev) => [...prev, ...newFiles])
      // Clear the input value to allow selecting the same file again if removed
      if (event.target) {
        event.target.value = ''
      }
    }
  }

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const startStream = useCallback(
    async (deviceId: string) => {
      // Stop any existing stream
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop())
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId } },
          audio: false,
        })
        setCameraStream(stream)
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch (err) {
        console.error(`[AgentChatInputArea] Error starting stream for device ${deviceId}:`, err)
        setCameraError(t('page.medconnectAgent.agentChatInputArea.cameraAccessFailedError', { message: (err as Error).message }))
      }
    },
    [cameraStream, t]
  )

  const openCamera = async () => {
    setCameraError(null)
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoInputDevices = devices.filter((d) => d.kind === 'videoinput')

      if (videoInputDevices.length === 0) {
        setCameraError(t('page.medconnectAgent.agentChatInputArea.noCamerasFoundError'))
        return
      }

      setVideoDevices(videoInputDevices)
      const rearCameraIndex = videoInputDevices.findIndex((d) => /back|environment/i.test(d.label))
      const initialIndex = rearCameraIndex !== -1 ? rearCameraIndex : 0
      setCurrentDeviceIndex(initialIndex)

      setCameraOpen(true)
      // We need to wait for the modal to be visible before starting the stream
      setTimeout(() => {
        startStream(videoInputDevices[initialIndex].deviceId)
      }, 100)
    } catch (err) {
      console.error('[AgentChatInputArea] Error accessing camera:', err)
      setCameraError(t('page.medconnectAgent.agentChatInputArea.cameraAccessFailedError', { message: (err as Error).message }))
    }
  }

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop())
      setCameraStream(null)
    }
    setCameraOpen(false)
    setVideoDevices([])
    setCurrentDeviceIndex(0)
  }

  const switchCamera = () => {
    if (videoDevices.length > 1) {
      const nextIndex = (currentDeviceIndex + 1) % videoDevices.length
      setCurrentDeviceIndex(nextIndex)
      startStream(videoDevices[nextIndex].deviceId)
    }
  }

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
          const photoFile = new File([blob], `camera-photo-${timestamp}.jpg`, { type: 'image/jpeg' })
          setSelectedFiles((prev) => [...prev, photoFile])
          closeCamera()
        }
      },
      'image/jpeg',
      0.95
    )
  }

  const handleInternalSendMessage = async () => {
    const textToSend = messageInput.trim()
    if ((!textToSend && selectedFiles.length === 0) || isLoading) return

    await onSendMessage(textToSend, selectedFiles)
    setMessageInput('')
    setSelectedFiles([])
    // Consider focusing the input again after send
    chatInputRef.current?.focus()
  }

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return '🖼'
    if (file.type === 'application/pdf') return '📄'
    if (file.type.includes('spreadsheet') || file.name.match(/\.(csv|xls|xlsx)$/)) return '📊'
    return '📎'
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <Box sx={sx}>
      {cameraError && (
        <Box sx={{ width: '100%', maxWidth: '800px', margin: '8px auto 0 auto' }}>
          <Typography color="error" variant="caption">
            {cameraError}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          p: compact ? 1 : 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          position: 'relative',
          bgcolor: compact ? 'transparent' : 'background.default',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '800px',
            p: compact ? '4px 8px' : '8px 12px',
            borderRadius: compact ? '16px' : '24px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white',
            boxShadow: 'none',
          }}
        >
          {/* Attached file chips */}
          {selectedFiles.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75, px: 0.5 }}>
              {selectedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: '8px',
                    bgcolor: 'grey.100',
                    maxWidth: compact ? '160px' : '200px',
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>{getFileIcon(file)}</Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'text.primary',
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', flexShrink: 0 }}>
                    {formatFileSize(file.size)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeSelectedFile(index)}
                    disabled={isLoading}
                    sx={{ p: 0, ml: -0.25, '& .MuiSvgIcon-root': { fontSize: '0.875rem' } }}
                  >
                    <CancelIcon sx={{ color: 'text.secondary' }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Input row */}
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Tooltip title={t('page.medconnectAgent.agentChatInputArea.attachFilesTooltip')}>
            <IconButton
              color="primary"
              aria-label={t('page.medconnectAgent.agentChatInputArea.uploadFileLabel')}
              component="label"
              disabled={isLoading}
              sx={{ mx: compact ? 0.5 : 1 }}
              size={compact ? 'small' : 'medium'}
            >
              <AttachFileIcon color="primary" fontSize={compact ? 'small' : 'medium'} />
              <VisuallyHiddenInput
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelection}
                multiple
                accept="image/*,application/pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
              />
            </IconButton>
          </Tooltip>

          {typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && (
            <Tooltip title={t('page.medconnectAgent.agentChatInputArea.takePhotoLabel', { defaultValue: 'Take photo' })}>
              <IconButton
                color="inherit"
                aria-label={t('page.medconnectAgent.agentChatInputArea.takePhotoLabel', { defaultValue: 'Take photo' })}
                disabled={isLoading}
                onClick={openCamera}
                sx={{ mx: compact ? 0.5 : 1, color: 'text.secondary' }}
                size={compact ? 'small' : 'medium'}
              >
                <CameraAltIcon color="primary" fontSize={compact ? 'small' : 'medium'} />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ flex: 1, mx: 1, display: 'flex', alignItems: 'center' }}>
            <textarea
              ref={chatInputRef}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleInternalSendMessage()
                }
              }}
              placeholder={placeholder || t('page.medconnectAgent.agentChatInputArea.placeholder', { defaultValue: 'Type your health question here...' })}
              disabled={isLoading}
              rows={1}
              style={{
                width: '100%',
                border: 'none',
                outline: 'none',
                padding: compact ? '4px 0' : '8px 0',
                fontSize: compact ? '0.85rem' : '1rem',
                resize: 'none',
                overflowY: 'auto',
                minHeight: '24px',
                maxHeight: '120px',
                backgroundColor: 'transparent',
                color: 'inherit',
                fontFamily: 'inherit',
                marginTop: 'auto',
                marginBottom: 'auto',
              }}
            />
          </Box>

          <Tooltip
            title={isLoading ? t('page.medconnectAgent.agentChatInputArea.sendingTooltip') : t('page.medconnectAgent.agentChatInputArea.sendMessageTooltip')}
          >
            <IconButton
              sx={{
                borderRadius: '50%',
                bgcolor: isLoading ? 'action.disabledBackground' : '#2AB6B7',
                color: isLoading ? 'text.disabled' : 'white',
                '&:hover': {
                  bgcolor: isLoading ? 'action.disabledBackground' : '#259AA4',
                },
                width: compact ? 30 : 36,
                height: compact ? 30 : 36,
              }}
              disabled={isLoading || (!messageInput.trim() && selectedFiles.length === 0)}
              onClick={handleInternalSendMessage}
            >
              {isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
          </Box>
        </Paper>
        {/* Instructions below input */}
        {!hideInstructions && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'text.secondary',
              mt: 1,
              px: 2,
              fontSize: '0.75rem',
            }}
          >
            {t('page.medconnectAgent.agentChatInputArea.inputInstructions', {
              defaultValue: 'Press Enter to send, Shift + Enter for new line',
            })}
          </Typography>
        )}
      </Box>

      <Modal open={cameraOpen} onClose={closeCamera} aria-labelledby="camera-modal-title">
        <Box sx={modalStyle}>
          <Typography id="camera-modal-title" variant="h6" component="h2">
            {t('page.medconnectAgent.agentChatInputArea.cameraModalTitle')}
          </Typography>
          <Box sx={{ width: '100%', position: 'relative' }}>
            <video ref={videoRef} autoPlay playsInline style={{ width: '100%', borderRadius: '8px', backgroundColor: '#000' }} />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {videoDevices.length > 1 && (
              <Tooltip title={t('page.medconnectAgent.agentChatInputArea.switchCameraTooltip')}>
                <IconButton
                  onClick={switchCamera}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <SwitchCameraIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 2 }}>
            <Button onClick={closeCamera} variant="outlined" startIcon={<CancelIcon />}>
              {t('page.medconnectAgent.agentChatInputArea.cancelButton')}
            </Button>
            <Button onClick={capturePhoto} variant="contained" color="primary" startIcon={<CameraAltIcon />}>
              {t('page.medconnectAgent.agentChatInputArea.captureButton')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}

export default AgentChatInputArea
